/**
 * Panel to the right of a collection view that displays info, comments, media preview, and close
 */
class InfoPanel extends BaseView {

	/**
	 *
	 */
	public collection: CollectionData;

	/**
	 * The panel element that displays the info
	 */
	public panel: HTMLElement;

	/**
	 * The parent for the panel element that displays the info
	 */
	public panelParent: any;

	/**
	 *
	 * @protected
	 */
	protected bannerHeight: number = 0;

	/**
	 * Nav drawer with the action items
	 * @private
	 */
	public navDrawer: NavDrawer;

	/**
	 *
	 * @private
	 */
	public activeItem: string | number;

	/**
	 * Current record being previewed in the media preview panel
	 */
	public recordPreview: any;

	/**
	 * is the panel open
	 */
	public isOpen: boolean = false;

	/**
	 *
	 * @private
	 */
	public panelModel: any;

	/**
	 * True if we should close the panel on resize,
	 * (for nearly all cases this should be true)
	 * @private
	 */
	private closeOnResize: boolean = true;


	/***
	 *
	 * @param collection
	 */
	constructor(collection: CollectionData) {
		super();

		this.collection = collection;
		this.view();
		this.setUpEvents();
	}

	/**
	 *
	 * @protected
	 */
	protected view() {
		this.panel = iwUI.createEl('div', {class: 'iPanel'});
		this.panelParent = document.getElementsByClassName('c-panel')[0];

		this.navDrawer = new NavDrawer([
			{
				id: 'info',
				cssClass: 'icon-btn icon-btn--iPanel iw_tool_tip_link',
				attrs: [
					{
						name: 'data-tool-tip',
						value: iwi18n.gettext('collections', 'Open info panel'),

					},
					{
						name: 'aria-label',
						value: iwi18n.gettext('collections', 'Open info panel')
					}
				],
			},
			{
				id: 'mediaItem',
				cssClass: 'icon-btn icon-btn--preview iw_tool_tip_link',
				show: this.collection.items && this.collection.items.length,
				attrs: [
					{
						name: 'data-tool-tip',
						value: iwi18n.gettext('collections', 'Open record item panel')
					},
					{
						name: 'aria-label',
						value: iwi18n.gettext('collections', 'Open record item panel'),
					}
				]
			},
			{
				id: 'close',
				cssClass: 'icon-css--close iw_tool_tip_link',
				spanClass: 'icon-css icon-css--chevrons',
				attrs: [
					{
						name: 'data-tool-tip',
						value: iwi18n.gettext('collections', 'Close info panel')
					},
					{
						name: 'aria-label',
						value: iwi18n.gettext('collections', 'Close info panel')
					}
				]
			},
		], this.changePanel.bind(this), 'iPanel-controls', 'collectionViewPanel');

		let breakPoint = iwCore.browser.breakPoint(window.innerWidth, window.innerWidth);
		if (breakPoint === 'desktop' && this.navDrawer.currentItem.id === 'close') {
			iwCore.addClassName(this.panelParent, 'iPanel-active');
			this.isOpen = true;
			let infoItem = this.navDrawer.getItemById('info');
			window.requestAnimationFrame(
				function(this: InfoPanel) {
					this.activeItem = null;
					this.navDrawer.changeItem(infoItem);
				}.bind(this));
		}

		this.mainContainer = this.navDrawer.mainContainer;
	}

	/**
	 *
	 * @protected
	 */
	protected setUpEvents() {
		let windowWidth = window.innerWidth;

		// when the window goes fullscreen it resizes, prevent the panel from closing
		// so the user can come back to the media they were previewing
		EVENT.attach(document, 'playerFullScreen', (e: CustomEvent) => {
			this.closeOnResize = !e.detail.open;
		});

		EVENT.attach(window, 'windowResized', (e: Event) => {
			if (this.closeOnResize) {
				// Check if the viewport width changes to make sure the resize event is not being triggered on iOS mobile scroll (when the browser address bar hides/ shows)
				if (windowWidth !== window.innerWidth) {
					let item = this.navDrawer.getItemById('close');
					this.navDrawer.changeItem(item);
					windowWidth = window.innerWidth;
				}
			}
		});

		iwKeys.attach("]",
		{
			fn: function(this: any) {
				if (this.isOpen) {
					this.changePanel.call(this, {id: 'close'});
				} else {
					this.changePanel.call(this, '')
				}
			}.bind(this),
			name: iwi18n.gettext('collections', 'Toggle info panel'),
			description: iwi18n.gettext('collections', 'Toggle info panel')});
	}

	/**
	 *
	 * @param item
	 * @private
	 */
	private changePanel(item: NavDrawerItem) {
		let isOpen = false,
			activeItem = item.id;

		if (item.id == 'close') {
			iwCore.removeClassName(this.panelParent, 'iPanel-active');
		} else if (this.activeItem === item.id) {
			activeItem = null;
			let closeItem = this.navDrawer.getItemById('close');
			this.navDrawer.changeItem(closeItem);
			return;
		} else {
			isOpen = true;
			iwCore.addClassName(this.panelParent, 'iPanel-active');
			iwToolTips.reInit();
		}
		this.setInactive();
		this.activeItem = activeItem;

		if (this.panelModel) {
			this.panelModel.close();
		}

		this.panelModel = null;

		switch (item.id) {
			case 'info':
				this.panelModel = new CollectionInfo(this.collection);
				iwCore.removeClassName(this.panel, 'iPanel--preview');
				break;
			case 'comments':
				this.panelModel = new CommentsPanel();
				break;
			case 'mediaItem':
				this.panelModel = new MediaPreviewPanel(this.collection);
				iwCore.addClassName(this.panel, 'iPanel--preview');
				break;
			case 'close':
				this.recordPreview = null;
				break;
		}

		if (this.panelModel) {
			iwCore.clearElement(this.panel);
			this.panel.appendChild(this.panelModel.frag);
			this.setInactive();
		}

		if (this.isOpen !== isOpen) {
			EVENT.custom('resizeTrigger');
		}
		this.isOpen = isOpen;
		EVENT.custom('InfoPanelOpen', {isOpen: isOpen}, this.panelParent);
	}

	/**
	 *
	 */
	public infoPanelHeight(height: number) {

		let viewType =  document.getElementById('my-collections');

		if (iwCore.hasClass(viewType, 'c-collections--results')) {

			let breakPoint = iwCore.browser.breakPoint(window.innerWidth, window.innerWidth),
				infoPanelControls = document.getElementsByClassName('iPanel-controls') [0];

			if (breakPoint !== 'desktop') {
				//plus 4px to account for borders?
				height -=  infoPanelControls.clientHeight + 4;
			}
			this.panel.style.height = height + 'px';
		}
	}

	/**
	 *
	 * @param height
	 */
	public resize(height: number) {
		this.infoPanelHeight(height);
	}

	/**
	 * Set preview active item to inactive once preview panel is closed
	 */
	public setInactive() {
		let items = document.getElementsByClassName('results-tile');
		for (let i = 0; i < items.length; i++) {
			iwCore.removeClassName(items[i], 'isActive')
		}
	}

	/**
	 * Update
	 */
	public update() {
		if (this.panelModel) {
			this.panelModel.update();
		}
	}

	/**
	 *
	 */
	public close() {
		if (this.panelModel) {
			this.panelModel.close();
		}
	}
}